import { useState, useEffect, useRef } from "react";
import api, { ApiResponse } from "api";
import { endpoints } from "serverDetails";

type GetFieldsResponse = {
  ReportInfo:
    | {
        CropName: string;
        FieldsId: number;
        FieldName: string;
        FieldReportDate: string;
        ReportLink: string | null;
        FieldValues: Record<string, string | number>;
        ThumbnailLink: string | null;
        FlightsId: number;
        ReportsId: number;
        bHasNotes: boolean;
      }[]
    | null;
  FieldNameFilterList: {
    FieldsId: number;
    FieldName: string;
  }[];
  CropTypeFilterList: { CropId: number; CropName: string }[];
  FlightDateFilterList: { FlightsId: number; FlightDate: string }[];
  SortByList: { Label: string; Value: string }[];
  ResultsCount: number;
};

type UseFetchFieldsReturnObject = {
  fields: GetFieldsResponse["ReportInfo"];
  validFieldList: {
    id: GetFieldsResponse["FieldNameFilterList"][number]["FieldsId"];
    name: GetFieldsResponse["FieldNameFilterList"][number]["FieldName"];
  }[];
  validCropTypeList: {
    id: GetFieldsResponse["CropTypeFilterList"][number]["CropId"];
    name: GetFieldsResponse["CropTypeFilterList"][number]["CropName"];
  }[];
  validDateList: Date[];
  validSortByList: {
    name: GetFieldsResponse["SortByList"][number]["Label"];
    value: GetFieldsResponse["SortByList"][number]["Value"];
    id: number;
  }[];
  resultsCount: GetFieldsResponse["ResultsCount"];
  noResults: boolean;
  loading: boolean;
  error: string | null;
};

/**
 * Custom hook for fetching fields
 */
export const useFetchFields = (
  FieldIds: { fieldID: number }[],
  FarmsId: number,
  Crops: {
    cropName: string;
  }[],
  SortBy: number,
  DateRange: { DateFrom: string; DateTo: string }[],
  SortDirection: string,
  PageNumber: string,
  PageSize: string,
  FilterChanged: boolean,
  setDateFilter: React.Dispatch<React.SetStateAction<string[]>>
): UseFetchFieldsReturnObject => {
  // State variables for fields data, loading status and error
  const [fields, setFields] = useState<UseFetchFieldsReturnObject["fields"]>(
    []
  );
  const [validFieldList, setValidFieldList] = useState<
    UseFetchFieldsReturnObject["validFieldList"]
  >([]);
  const [validCropTypeList, setValidCropTypeList] = useState<
    UseFetchFieldsReturnObject["validCropTypeList"]
  >([]);
  const [validDateList, setValidDateList] = useState<
    UseFetchFieldsReturnObject["validDateList"]
  >([]);
  const [validSortByList, setValidSortByList] = useState<
    UseFetchFieldsReturnObject["validSortByList"]
  >([]);
  const [noResults, setNoResults] = useState<
    UseFetchFieldsReturnObject["noResults"]
  >(false);
  const [resultsCount, setResultsCount] = useState<
    UseFetchFieldsReturnObject["resultsCount"]
  >(0);
  const [loading, setLoading] = useState<UseFetchFieldsReturnObject["loading"]>(
    false
  );
  const [error, setError] = useState<UseFetchFieldsReturnObject["error"]>(null);
  const timeoutRef = useRef<number>();

  // Effect hook for fetching fields when parameters change
  useEffect(() => {
    // Set loading status to true

    let sortByValue = "Alphabetically";
    if (validSortByList && validSortByList.length > 0) {
      const matchingSort = validSortByList.find(({ id }) => id === SortBy);
      if (matchingSort) {
        sortByValue = matchingSort.value;
      }
    }

    const payload = {
      FieldIds,
      FarmsId,
      Crops,
      DateRange,
      SortBy: sortByValue,
      SortDirection,
      PageNumber,
      PageSize,
      setDateFilter,
    };

    async function fetchData() {
      setLoading(true);

      try {
        const response = await api.post<
          ApiResponse<GetFieldsResponse, unknown>
        >(endpoints.reportsModule.getReportsPageInfo, payload);
        if (
          response &&
          response.data &&
          response.data.details &&
          response.data.details.ReportInfo
        ) {
          setNoResults(false);

          setFields(response.data.details.ReportInfo);
          setValidFieldList(
            response.data.details.FieldNameFilterList.map(field => ({
              id: field.FieldsId,
              name: field.FieldName,
            }))
          );
          setValidCropTypeList(
            response.data.details.CropTypeFilterList.map(crop => ({
              id: crop.CropId,
              name: crop.CropName,
            }))
          );
          setValidDateList(
            response.data.details.FlightDateFilterList.map(
              ({ FlightDate }) => new Date(`${FlightDate}`)
            )
          );
          setValidSortByList(
            response.data.details.SortByList.map((sortByItem, idx) => ({
              name: sortByItem.Label,
              value: sortByItem.Value,
              id: idx,
            }))
          );
          setResultsCount(response.data.details.ResultsCount);
        } else {
          setNoResults(true);
        }

        setLoading(false);
      } catch (error) {
        setError("An error occurred while fetching fields data.");
        // setError(error);
        setLoading(false);
      }
    }

    if (FarmsId) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        fetchData();
      }, 150);
    }
  }, [FilterChanged, FarmsId]);

  return {
    fields,
    validFieldList,
    validCropTypeList,
    validDateList,
    validSortByList,
    resultsCount,
    noResults,
    loading,
    error,
  };
};
